import Dexie from "dexie";
import { ConsultationDexieDb } from "../db/ConsultationDexieDb";
import { AudioSegment } from "./types";

export class AudioSegmentRepo {
  constructor(private readonly db: ConsultationDexieDb) {}

  async put(segment: AudioSegment): Promise<number> {
    const key = await this.db.segments.put(segment);
    return typeof key === "number" ? key : Number(key);
  }

  async bulkPut(segments: AudioSegment[]): Promise<void> {
    if (segments.length === 0) {
      return;
    }

    await this.db.segments.bulkPut(segments);
  }

  async listBySession(sessionId: string): Promise<AudioSegment[]> {
    return this.db.segments
      .where("[sessionId+segmentId]")
      .between([sessionId, Dexie.minKey], [sessionId, Dexie.maxKey])
      .sortBy("segmentId");
  }

  async getFirstSeqAtOrAfterMs(
    sessionId: string,
    replayFromMs: number
  ): Promise<number | null> {
    const segment = await this.db.segments
      .where("[sessionId+endMs]")
      .between([sessionId, replayFromMs], [sessionId, Dexie.maxKey])
      .first();

    return segment ? segment.startSeq : null;
  }

  async getReplayBatch(
    sessionId: string,
    fromSeq: number,
    maxSegments: number,
    maxBytes: number
  ): Promise<AudioSegment[]> {
    const candidates = await this.db.segments
      .where("[sessionId+endSeq]")
      .between([sessionId, fromSeq], [sessionId, Dexie.maxKey])
      .toArray();

    const sorted = candidates.sort((left, right) => left.startSeq - right.startSeq);
    const batch: AudioSegment[] = [];
    let accumulatedBytes = 0;

    for (let index = 0; index < sorted.length; index += 1) {
      const segment = sorted[index];
      batch.push(segment);
      accumulatedBytes += segment.byteLength;

      if (batch.length >= maxSegments || accumulatedBytes >= maxBytes) {
        break;
      }
    }

    return batch;
  }

  async clearReplayFlagThroughMs(
    sessionId: string,
    replaySafeBoundaryMs: number
  ): Promise<number> {
    const candidates = await this.db.segments
      .where("[sessionId+endMs]")
      .between([sessionId, Dexie.minKey], [sessionId, replaySafeBoundaryMs])
      .toArray();

    const eligible = candidates.filter((segment) => segment.keepForReplay);

    if (eligible.length === 0) {
      return 0;
    }

    await this.db.transaction("rw", this.db.segments, async () => {
      for (let index = 0; index < eligible.length; index += 1) {
        const segment = eligible[index];
        await this.db.segments.update(segment.id as number, {
          keepForReplay: false,
        });
      }
    });

    return eligible.length;
  }

  async clearArchiveFlags(sessionId: string, uploadedAt: number): Promise<number> {
    const candidates = await this.db.segments
      .where("[sessionId+keepForArchive]")
      .equals([sessionId, true] as any)
      .toArray();

    if (candidates.length === 0) {
      return 0;
    }

    await this.db.transaction("rw", this.db.segments, async () => {
      for (let index = 0; index < candidates.length; index += 1) {
        const segment = candidates[index];
        await this.db.segments.update(segment.id as number, {
          keepForArchive: false,
          uploadedAt,
        });
      }
    });

    return candidates.length;
  }

  async listCompactionEligible(sessionId: string): Promise<AudioSegment[]> {
    return this.db.segments
      .where("[sessionId+keepForReplay]")
      .equals([sessionId, false] as any)
      .and((segment) => !segment.keepForArchive)
      .toArray();
  }

  async deleteByIds(ids: number[], deletedAt: number): Promise<void> {
    if (ids.length === 0) {
      return;
    }

    await this.db.transaction("rw", this.db.segments, async () => {
      for (let index = 0; index < ids.length; index += 1) {
        await this.db.segments.update(ids[index], { deletedAt });
      }

      await this.db.segments.bulkDelete(ids);
    });
  }
}
